/**
 * starEaterConstants.ts
 * Tuning knobs for the World 2 `StarEater` scout — the small, fast
 * incursion enemy that closes out Level 2-4, and its three-phase
 * predator AI (DRIFT → TELEGRAPH → SCAN → DASH). Split out of
 * `spaceEnemyConstants.ts` so each enemy kind owns its own constants
 * file (Phase 5 Tech Debt — file length cap split, see ROADMAP.md).
 *
 * Consumers should keep importing from `spaceEnemyConstants` — that
 * module re-exports every symbol here so the public surface is
 * unchanged.
 *
 * @module starEaterConstants
 */

// ── StarEater scout ──────────────────────────────────────────────
//
// Phase 4 — Level 2-4 "Boss Approach" closing scout fleet. Thematic
// foreshadowing of the World 5 boss (the Star-Eater) — small, dark,
// fast incursion enemies that arrive in tight formations.

/** Hitbox width in logical px. Narrower than fighter (36) so the scout
 *  reads as a smaller, faster silhouette at a glance. */
export const STAR_EATER_WIDTH = 28;

/** Hitbox height in logical px. Shorter than fighter (28) for the same
 *  "fast scout" silhouette. */
export const STAR_EATER_HEIGHT = 20;

/** One ship-projectile hit kills a scout. The threat is the speed and
 *  the formation density, not durability. */
export const STAR_EATER_HP = 1;

/**
 * Leftward drift relative to world space (px / s). Faster than the
 * fighter (120) so the scout reads as an aggressive incursion. With the
 * 80 px/s auto-scroll that's a 240 px/s apparent on-screen velocity —
 * the player has less reaction time than against a fighter, which is
 * the entire point of this enemy type.
 */
export const STAR_EATER_DRIFT_SPEED = 160;

// ── Star-Eater Predator AI ──────────────────────────────────────────
//
// Three-phase predator sequence triggered by elapsed-time-since-spawn:
//   DRIFT → TELEGRAPH → SCAN → DASH → DRIFT (committed)
//
// Time-based trigger (not camera-X based) so the windup is deterministic
// from the moment the scout is acquired — pure local state, trivially
// testable, and consistent regardless of camera scroll rate. The total
// pre-dash windup (DRIFT_PRE + TELEGRAPH + SCAN) is intentionally long
// enough for the player to shoot the scout before it commits, since the
// scout is 1 HP and the dash is the lethal payload.

/**
 * Seconds after `spawn()` before the scout transitions from DRIFT into
 * TELEGRAPH. With STAR_EATER_DRIFT_SPEED=160 + 80 px/s camera scroll,
 * the scout travels ~120 px on-screen in this window — enough that it
 * has fully entered the viewport before showing the telegraph.
 */
export const STAR_EATER_DRIFT_PRE_DURATION_S = 0.5;

/**
 * Telegraph window length in seconds. Scout holds position (vx=vy=0) so
 * the player gets a clear "warning" frame to identify the threat before
 * the SCAN+DASH commits.
 */
export const STAR_EATER_TELEGRAPH_DURATION_S = 0.3;

/**
 * Scan window length in seconds. Scout is still stationary but locks
 * onto the ship's Y at the moment the SCAN phase starts. The lock is a
 * one-shot snapshot, not continuous tracking, so the player can dodge
 * by repositioning vertically during the SCAN window before the DASH
 * commits to the locked Y.
 */
export const STAR_EATER_SCAN_DURATION_S = 0.2;

/**
 * Dash window length in seconds. Scout travels at DASH_SPEED toward the
 * locked Y. After the dash window the scout returns to a committed
 * DRIFT state (left-drift only, no second dash) until the left-cull
 * recycles it.
 */
export const STAR_EATER_DASH_DURATION_S = 0.8;

/**
 * Horizontal dash speed in px/s (world-space, leftward). 2× the drift
 * speed so the dash reads as a clear escalation from the prior pacing.
 * Apparent on-screen velocity during the dash is 320 + 80 = 400 px/s.
 */
export const STAR_EATER_DASH_SPEED = 320;

/**
 * Maximum vertical dash velocity in px/s. The dash vy is computed at
 * SCAN→DASH transition as `sign(targetY − scoutY) * DASH_VY_MAX`,
 * clamped so the scout never overshoots the locked Y mid-dash even if
 * the dash distance is short. Tuned so the dash arrives near the locked
 * Y over the DASH_DURATION window from anywhere in the viewport.
 */
export const STAR_EATER_DASH_VY_MAX = 200;

/** Discriminator for Star-Eater predator AI sub-state. */
export type StarEaterAiPhase = 'DRIFT_PRE' | 'TELEGRAPH' | 'SCAN' | 'DASH' | 'DRIFT_POST';
